/**
 * Fractional ranking for Implementation Plans card ordering.
 *
 * Implements ADR: docs/roadmap/ADR.ImplementationPlansView.md §1a-§1c.
 *
 * ## R1 (invariant, §1a)
 * *Every* rank on the axis -- stored (`rankBetween` output) or implicit (`implicitRank`
 * output) -- is non-empty and never ends in `'0'`. A trailing zero is numerically
 * redundant (`"i0"` and `"i"` denote the same value), so a rank violating R1 makes
 * lexicographic string comparison diverge from numeric comparison. That breaks the
 * total order in §1c and can make `rankBetween` non-terminating (see the guard
 * rationale on `rankBetween` below). R1 must therefore be enforced by *both*
 * producers: `rankBetween` by construction (the loop only returns on `mid > da >= 0`,
 * i.e. a nonzero digit) and `implicitRank` by stripping trailing zeros from its raw
 * base-36 expansion.
 */

/** Base-36 alphabet. ASCII order of these characters matches digit order, so plain
 * lexicographic string comparison equals numeric comparison (§1a). */
const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const BASE = ALPHABET.length; // 36

/**
 * Floor on the iteration cap for `rankBetween`'s digit-comparison loop. Pure
 * headroom for short inputs against future changes to the algorithm itself.
 *
 * The actual cap is input-derived: `max(HARD_ITERATION_CAP_FLOOR, max(len(before),
 * len(after)) + 2)`. This is exactly one iteration above the proven bound
 * `max(len(before), len(after)) + 1` (§1b, verified by execution over 20,000
 * randomized insert sequences), so it fires **if and only if** a precondition was
 * violated (i.e. the inputs are not R1-compliant or `before >= after`). A
 * precondition violation does not run merely *long* — it runs *forever*: once both
 * inputs are exhausted, `da == db == 0` at every subsequent index, so the loop never
 * exits. Throwing here converts that into a caught assertion instead of a frozen tab.
 *
 * See ADR §1b, Amendment 2026-08-04.
 */
const HARD_ITERATION_CAP_FLOOR = 64;

/** Base-36 digit width of the raw expansion `implicitRank` starts from (§1c). */
const IMPLICIT_RANK_INITIAL_WIDTH = 4;

/**
 * Safety limit on how many times `implicitRank` may double its expansion width while
 * hunting for a non-zero digit (the widen-and-retry step, §1c). This is only ever
 * exercised at or above the `36^4` precision ceiling; the cap exists purely so a future
 * change to the ceiling logic fails loudly instead of looping forever.
 */
const IMPLICIT_RANK_MAX_WIDTH_DOUBLINGS = 20;

/**
 * Returns the base-36 digit value (0-35) of `s` at index `i`, or `0` if `i` is past the
 * end of `s` -- the "absent digit" convention used throughout §1b.
 */
function digitAt(s: string, i: number): number {
  if (i >= s.length) return 0;
  const code = s.charCodeAt(i);
  if (code >= 48 && code <= 57) return code - 48; // '0'-'9'
  if (code >= 97 && code <= 122) return code - 97 + 10; // 'a'-'z'
  throw new Error(`rankBetween: invalid rank character "${s[i]}" in "${s}"`);
}

/**
 * Returns a base-36 rank string strictly between `before` and `after`.
 *
 * Absent-neighbor semantics (§1b): an absent `before` means the lower bound `0`; an
 * absent `after` means the upper bound `1`. Both absent returns `"i"` (18/36 = 0.5).
 *
 * **Precondition: `before` must be strictly less than `after`, numerically.** Under
 * total R1 (§1a), lexicographic string comparison equals numeric comparison, so the
 * precondition is enforced as a string comparison -- but only because R1 is total.
 * Violating the precondition produces a non-terminating loop rather than a wrong
 * answer, so it is guarded explicitly rather than assumed: equal or inverted inputs
 * throw immediately rather than reaching the midpoint loop. Callers that hit two equal
 * ranks must route through the §1d tied-group rule instead of calling `rankBetween`.
 */
export function rankBetween(before?: string, after?: string): string {
  const a = before ?? '';
  let b = after ?? '';

  if (b !== '' && a >= b) {
    throw new Error(
      `rankBetween: precondition violated -- "before" ("${a}") must sort strictly before ` +
        `"after" ("${b}"). Equal ranks must be resolved via the §1d tied-group rule, not rankBetween.`,
    );
  }

  // Derive the iteration cap from the input lengths. The cap must be computed before
  // the loop (b is reassigned to '' inside the descent branch), and it sits exactly
  // one iteration above the proven bound max(len(a), len(b)) + 1, so it fires iff a
  // precondition was violated (§1b, Amendment 2026-08-04).
  const cap = Math.max(HARD_ITERATION_CAP_FLOOR, Math.max(a.length, b.length) + 2);

  let out = '';
  for (let i = 0; i < cap; i++) {
    const da = digitAt(a, i);
    const db = b === '' ? BASE : digitAt(b, i);
    if (da === db) {
      out += ALPHABET[da];
      continue;
    }
    const mid = Math.floor((da + db) / 2);
    if (mid > da) {
      // mid > da >= 0, so ALPHABET[mid] is never '0' -> R1 holds by construction.
      return out + ALPHABET[mid];
    }
    // da and db are digit-adjacent (db === da + 1): descend into `a`, and treat the
    // upper bound as absent (1.0) from here on.
    out += ALPHABET[da];
    b = '';
  }

  throw new Error(
    `rankBetween: exceeded the ${cap}-iteration cap (derived from input lengths) for before=${JSON.stringify(
      before ?? '',
    )}, after=${JSON.stringify(
      after ?? '',
    )}. This should be unreachable for R1-compliant inputs (proven bound: ` +
      'max(len(before), len(after)) + 1); a non-R1-compliant or otherwise malformed input likely reached rankBetween directly.',
  );
}

/**
 * Returns the base-36 digits of `numerator / denominator`, `width` digits deep,
 * computed with integer (remainder-based) long division so precision never depends on
 * floating point rounding.
 */
function base36Fraction(numerator: number, denominator: number, width: number): string {
  let digits = '';
  let remainder = numerator;
  for (let d = 0; d < width; d++) {
    remainder *= BASE;
    const digit = Math.floor(remainder / denominator);
    digits += ALPHABET[digit];
    remainder %= denominator;
  }
  return digits;
}

/**
 * Derives a rank for the `i`-th (0-indexed) of `N` unranked cards, matching Map3 row
 * order (§1c). Pure function of `(i, N)` -- every client computes the same value, so
 * unranked cards never require a write, backfill, or migration.
 *
 * `implicitRank(i, N) = base36Fraction((i + 1) / (N + 1), width = 4)` with trailing
 * `'0'`s stripped (R1). Strictly monotonic in `i` for any `N < 36^4`. At or above that
 * precision ceiling, the initial 4-digit expansion can be all-zero for small `i`
 * (the raw expansion truncates to nothing), so the width is doubled and retried until a
 * non-zero expansion is found (the widen-and-retry step, §1c).
 */
export function implicitRank(i: number, N: number): string {
  if (!Number.isInteger(i) || !Number.isInteger(N) || N < 1 || i < 0 || i >= N) {
    throw new Error(`implicitRank: invalid arguments i=${i}, N=${N} (require integers with 0 <= i < N)`);
  }

  const numerator = i + 1;
  const denominator = N + 1;
  let width = IMPLICIT_RANK_INITIAL_WIDTH;

  for (let attempt = 0; attempt <= IMPLICIT_RANK_MAX_WIDTH_DOUBLINGS; attempt++) {
    const stripped = base36Fraction(numerator, denominator, width).replace(/0+$/, '');
    if (stripped !== '') {
      return stripped;
    }
    width *= 2;
  }

  throw new Error(`implicitRank: failed to produce a non-zero expansion for i=${i}, N=${N}`);
}
